import api from "api/axios";

const BASE = "/admin/external-labs";

const pickErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

const mutate = async (request, fallbackMessage) => {
  try {
    const response = await request();
    return { ok: true, data: response?.data?.data ?? null };
  } catch (error) {
    return { ok: false, error: pickErrorMessage(error, fallbackMessage) };
  }
};

export const listExternalLabs = async (params = {}) => {
  try {
    const response = await api.get(BASE, { params });
    const payload = response?.data ?? {};
    return {
      ok: true,
      data: Array.isArray(payload.data) ? payload.data : [],
      meta: payload.meta || { total: 0, page: 1, perPage: 20, totalPages: 0 },
    };
  } catch (error) {
    return {
      ok: false,
      data: [],
      meta: { total: 0, page: 1, perPage: params?.perPage || 20, totalPages: 0 },
      error: pickErrorMessage(error, "Unable to load external labs."),
    };
  }
};

export const getExternalLab = (id) =>
  mutate(() => api.get(`${BASE}/${id}`), "Unable to load the lab.");

export const createExternalLab = (payload) =>
  mutate(() => api.post(BASE, payload), "Unable to register the lab.");

export const updateExternalLab = (id, payload) =>
  mutate(() => api.put(`${BASE}/${id}`, payload), "Unable to update the lab.");

export const approveExternalLab = (id, note = "") =>
  mutate(
    () => api.post(`${BASE}/${id}/approve`, { note }),
    "Unable to approve the lab.",
  );

export const rejectExternalLab = (id, reason) =>
  mutate(
    () => api.post(`${BASE}/${id}/reject`, { reason }),
    "Unable to reject the lab.",
  );

export const returnExternalLabForCorrection = (id, reason) =>
  mutate(
    () => api.post(`${BASE}/${id}/return-for-correction`, { reason }),
    "Unable to return the lab for correction.",
  );

export const resubmitExternalLab = (id, note = "") =>
  mutate(
    () => api.post(`${BASE}/${id}/resubmit`, { note }),
    "Unable to resubmit the lab.",
  );

export const uploadExternalLabDocument = (id, file) => {
  const body = new FormData();
  body.append("document", file);
  return mutate(
    () =>
      api.post(`${BASE}/${id}/documents`, body, {
        headers: { "Content-Type": "multipart/form-data" },
      }),
    "Unable to upload the document.",
  );
};

export const getExternalLabDocumentUrl = (id, documentId) =>
  mutate(
    () => api.get(`${BASE}/${id}/documents/${documentId}/url`),
    "Unable to open the document.",
  );

export const deleteExternalLabDocument = (id, documentId) =>
  mutate(
    () => api.delete(`${BASE}/${id}/documents/${documentId}`),
    "Unable to remove the document.",
  );

export const listExternalLabClinicOptions = (id, search = "") =>
  mutate(
    () =>
      api.get(`${BASE}/${id}/clinic-options`, {
        params: search ? { search } : {},
      }),
    "Unable to load hospitals / clinics.",
  );

export const listExternalLabMappings = (id, includeInactive = false) =>
  mutate(
    () =>
      api.get(`${BASE}/${id}/mappings`, {
        params: includeInactive ? { includeInactive: true } : {},
      }),
    "Unable to load mapped hospitals / clinics.",
  );

export const mapExternalLabToClinics = (id, clinicIds) =>
  mutate(
    () => api.post(`${BASE}/${id}/mappings`, { clinicIds }),
    "Unable to map the lab.",
  );

export const unlinkExternalLabMapping = (id, mappingId) =>
  mutate(
    () => api.patch(`${BASE}/${id}/mappings/${mappingId}/unlink`),
    "Unable to unlink the hospital / clinic.",
  );
